import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export interface UserAccommodationBooking {
  id: string;
  accommodation_id: string;
  producer_id: string;
  check_in: string;
  check_out: string;
  guests: number;
  guest_name: string | null;
  status: string;
  booking_code: string;
  confirmation_token: string;
  created_at: string;
  cancelled_at: string | null;
  payment_status: string;
  amount_paid_ore: number;
  accommodation_title: string;
  producer_name: string;
}

interface UseMyAccommodationBookingsOptions {
  enabled?: boolean;
}

export function useMyAccommodationBookings(options: UseMyAccommodationBookingsOptions = {}) {
  const { user } = useAuth();
  const queryEnabled = options.enabled ?? true;

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-accommodation-bookings', user?.id],
    queryFn: async () => {
      if (!user?.email) return [];

      const { data: rows, error: fetchError } = await supabase
        .from('accommodation_bookings')
        .select(`
          id, accommodation_id, producer_id,
          check_in, check_out, guests, guest_name,
          status, booking_code, confirmation_token,
          created_at, cancelled_at,
          payment_status, amount_paid_ore,
          producer_accommodations!accommodation_bookings_accommodation_id_fkey ( title ),
          producers!accommodation_bookings_producer_id_fkey ( name )
        `)
        .eq('guest_email_norm', user.email.toLowerCase())
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      if (!rows || rows.length === 0) return [];

      // Aggregate payments from canonical source
      const bookingIds = rows.map((r: any) => r.id);
      const { data: paymentRows } = await supabase
        .from('accommodation_booking_payments')
        .select('booking_id, amount_ore')
        .in('booking_id', bookingIds);

      const paidByBooking = new Map<string, number>();
      for (const p of paymentRows ?? []) {
        paidByBooking.set(p.booking_id, (paidByBooking.get(p.booking_id) ?? 0) + p.amount_ore);
      }

      return rows.map((r: any): UserAccommodationBooking => ({
        id: r.id,
        accommodation_id: r.accommodation_id,
        producer_id: r.producer_id,
        check_in: r.check_in,
        check_out: r.check_out,
        guests: r.guests,
        guest_name: r.guest_name,
        status: r.status,
        booking_code: r.booking_code,
        confirmation_token: r.confirmation_token,
        created_at: r.created_at,
        cancelled_at: r.cancelled_at,
        payment_status: r.payment_status ?? 'unpaid',
        amount_paid_ore: paidByBooking.get(r.id) ?? 0,
        accommodation_title: r.producer_accommodations?.title ?? 'Boende',
        producer_name: r.producers?.name ?? 'Okänd värd',
      }));
    },
    enabled: queryEnabled && !!user,
  });

  return {
    bookings: data ?? [],
    isLoading,
    error: error as Error | null,
  };
}
